import { FormEvent, useEffect, useState } from 'react';
import { Alert, Col, Row, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckboxField } from '../../../../common/components/forms/CheckboxField';
import { TextField } from '../../../../common/components/forms/TextField';
import BackButton from '../../../../common/filter-table/BackButton';
import DeleteButton from '../../../../common/filter-table/DeleteButton';
import SaveButton from '../../../../common/filter-table/SaveButton';
import { Private_Routes } from '../../../../constants';
import { usePayrollNoveltyFlows } from '../../hooks/usePayrollNoveltyFlows';
import { GroupViewModel } from '../../models/GroupTree';
import {
    GroupModel,
    PayrollNoveltyFlow,
    UserModel,
} from '../../models/payrollNoveltyFlow';
import { UserItemModel } from '../../models/UserItem';
import {
    AddGroupTreeModal,
    AddGroupTreeModalProps,
} from '../modals/AddGroupTreeModal';
import { AddUserModal, AddUserModalProps } from '../modals/AddUserModal';
import { ConfirmModal, ConfirmModalProps } from '../modals/ConfirmModal';

interface Props {
    isNew: boolean;
    data?: PayrollNoveltyFlow;
}

export const PayrollNoveltyFlowsForm = ({ data, isNew }: Props) => {
    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();

    const {
        updatePayrollNoveltyFlow,
        deletePayrollNoveltyFlow,
        getUsersApproversFlow,
        getGroupsFlow,
        createPayrollNoveltyFlow,
    } = usePayrollNoveltyFlows();

    const [payrollNoveltyFlow, setPayrollNoveltyFlow] = useState<PayrollNoveltyFlow>(
        data ? data : { users: [], groups: [], name: '', isForAllGroups: false }
    );
    const [errorMessage, setErrorMessage] = useState<string | null>();
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [detailsConfirmModal, setDetailsConfirmModal] =
        useState<ConfirmModalProps>({} as ConfirmModalProps);

    const [usersApproversModal, setUsersApproversModal] =
        useState<AddUserModalProps>({} as AddUserModalProps);

    const [groupsModal, setGroupsModal] = useState<AddGroupTreeModalProps>(
        {} as AddGroupTreeModalProps
    );

    const [groupsTreeList, setGroupsTreeList] = useState<GroupViewModel[]>([]);
    const [usersApprovers, setUsersApprovers] = useState<UserItemModel[]>([]);

    useEffect(() => {
        getUsersApproversFlow()
            .then((res) => setUsersApprovers(res))
            .catch((err) => toast.error(err.response.data));

        getGroupsFlow()
            .then((res) => setGroupsTreeList(res))
            .catch((err) => toast.error(err.response.data));
    }, []);

    const handleInputChange = (
        name: string,
        value: string | number | boolean | File
    ) => {
        setPayrollNoveltyFlow({ ...payrollNoveltyFlow, [name]: value });

        if (name == 'isForAllGroups' && value) {
            handlerDeleteAllGroup();
        }
    };

    useEffect(() => {
        if (!validateRequiredContractFields(payrollNoveltyFlow)) {
            setErrorMessage('Completa todos los campos requeridos');
        } else if (
            !payrollNoveltyFlow.isForAllGroups &&
            payrollNoveltyFlow?.groups?.length === 0
        ) {
            setErrorMessage(
                'Debes agregar mínimo un grupo o tener el check "Válido para todos los grupos"'
            );
        } else if (payrollNoveltyFlow?.users?.length === 0) {
            setErrorMessage('Debes agregar mínimo un usuario');
        } else {
            setErrorMessage('');
        }
    }, [payrollNoveltyFlow]);

    const validateRequiredContractFields = (
        contract: PayrollNoveltyFlow
    ): boolean => {
        const requiredFields = ['name'];

        return requiredFields.every(
            (field) =>
                contract[field as keyof PayrollNoveltyFlow] !== '' &&
                contract[field as keyof PayrollNoveltyFlow] !== null &&
                contract[field as keyof PayrollNoveltyFlow] !== undefined
        );
    };

    const renderOrderElements = (user: UserModel, index: number) => {
        const isFirst = index === 0;
        const isLast = index === payrollNoveltyFlow?.users?.length - 1;

        return (
            <div>
                {isFirst ? (
                    <i
                        className='fs-17 ri-arrow-down-s-line'
                        onClick={() => downPriority(user, index)}
                        style={{ cursor: 'pointer' }}
                    />
                ) : isLast ? (
                    <i
                        className='fs-17 ri-arrow-up-s-line'
                        onClick={() => upPriority(user, index)}
                        style={{ cursor: 'pointer' }}
                    />
                ) : (
                    <>
                        <i
                            className='fs-17 ri-arrow-down-s-line'
                            onClick={() => downPriority(user, index)}
                            style={{ cursor: 'pointer' }}
                        />
                        <i
                            className='fs-17 ri-arrow-up-s-line'
                            onClick={() => upPriority(user, index)}
                            style={{ cursor: 'pointer' }}
                        />
                    </>
                )}
                <i
                    className='fs-17 ri-close-circle-fill text-danger'
                    onClick={() => removeUser(user, index)}
                    style={{ cursor: 'pointer' }}
                />
            </div>
        );
    };

    const upPriority = (user: UserModel, index: number) => {
        if (index === 0) return;

        const newUsers = [...payrollNoveltyFlow.users];

        const userToMove = newUsers[index];
        const userBefore = newUsers[index - 1];

        newUsers[index] = userBefore;
        newUsers[index - 1] = userToMove;

        userToMove.stage -= 1;
        userBefore.stage += 1;

        setPayrollNoveltyFlow((prev) => ({ ...prev, users: newUsers }));
    };

    const downPriority = (user: UserModel, index: number) => {
        if (index === payrollNoveltyFlow?.users?.length - 1) return;

        const newUsers = [...payrollNoveltyFlow?.users];

        const userToMove = newUsers[index];
        const userAfter = newUsers[index + 1];

        newUsers[index] = userAfter;
        newUsers[index + 1] = userToMove;

        userToMove.stage += 1;
        userAfter.stage -= 1;

        setPayrollNoveltyFlow((prev) => ({ ...prev, users: newUsers }));
    };

    const handlerDeleteGroup = (groupId: number) => {
        let newGroups = payrollNoveltyFlow?.groups.filter(
            (group) => group.groupId !== groupId
        );

        setPayrollNoveltyFlow((prev) => ({ ...prev, groups: newGroups }));
    };

    const handlerDeleteAllGroup = () => {
        setPayrollNoveltyFlow((prev) => ({ ...prev, groups: [] }));
    };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setIsSaving(true);

        if (!isNew && id) {
            updatePayrollNoveltyFlow(id.toString(), payrollNoveltyFlow)
                .then((res) => {
                    toast.success(
                        'Se actualizó el flujo de aprobaciones correctamente'
                    );
                    navigate(
                        Private_Routes.PAYROLL_NOVELTY_FLOWS.edit.replace(
                            ':id',
                            id ?? ''
                        )
                    );
                })
                .catch((err) => setErrorMessage(err.response.data))
                .finally(() => setIsSaving(false));
        } else {
            createPayrollNoveltyFlow(payrollNoveltyFlow)
                .then((res) => {
                    toast.success('Se creó el flujo de aprobaciones correctamente');
                    navigate(Private_Routes.PAYROLL_NOVELTY_FLOWS.index);
                })
                .catch((err) => setErrorMessage(err.response.data))
                .finally(() => setIsSaving(false));
        }
    };

    const onDeletePayrollNoveltyFlow = () => {
        if (id) {
            setIsDeleting(true);
            onCloseConfirmDeleteModal();
            deletePayrollNoveltyFlow(id)
                .then((res) => {
                    toast.success(
                        'Se eliminó el flujo de aprobaciones correctamente'
                    );
                    navigate(Private_Routes.PAYROLL_NOVELTY_FLOWS.index);
                })
                .catch((err) => setErrorMessage(err.response.data))
                .finally(() => setIsDeleting(false));
        }
    };

    const onAddGroupFlow = (data: GroupViewModel[]) => {
        let groupsToDelete = payrollNoveltyFlow?.groups?.filter(
            (u) => !data.map((e) => e.id).includes(u.groupId)
        );

        let usersToAdd = data.filter(
            (d) =>
                !payrollNoveltyFlow?.groups
                    .map((u) => u.groupId)
                    .includes(Number(d.id))
        );

        let newList = payrollNoveltyFlow?.groups.filter(
            (group) => !groupsToDelete.includes(group)
        );

        let adaptedGroups: GroupModel[] = usersToAdd?.map((d) => {
            return {
                groupId: Number(d.id),
                groupName: d.text,
            };
        });

        newList = [...newList, ...adaptedGroups];

        setPayrollNoveltyFlow({ ...payrollNoveltyFlow, groups: newList });

        onCloseGroupsModal();
    };

    const adjustUserStages = (users: UserModel[]): UserModel[] => {
        return users?.map((user, index) => ({
            ...user,
            stage: index + 1,
        }));
    };

    const removeUser = (user: UserModel, index: number) => {
        let newList = payrollNoveltyFlow?.users?.filter(
            (u) => u.fullName !== user.fullName
        );

        newList = adjustUserStages(newList);

        setPayrollNoveltyFlow({ ...payrollNoveltyFlow, users: newList });
    };

    const onAddUserApproversFlow = (data: UserItemModel[]) => {
        let usersToDelete = payrollNoveltyFlow?.users?.filter(
            (u) => !data.map((e) => e.fullName).includes(u.fullName)
        );

        let usersToAdd = data.filter(
            (d) =>
                !payrollNoveltyFlow?.users
                    .map((u) => u.fullName)
                    .includes(d.fullName)
        );

        let newList = payrollNoveltyFlow?.users.filter(
            (user) => !usersToDelete.includes(user)
        );

        newList = adjustUserStages(newList);

        let lastElement = newList.at(-1);

        let adaptedUsers: UserModel[] = usersToAdd.map((d) => {
            return {
                userId: d.id,
                fullName: d.fullName,
                stage: lastElement ? lastElement.stage + 1 : 0,
            };
        });

        newList = [...newList, ...adaptedUsers];

        newList = adjustUserStages(newList);

        setPayrollNoveltyFlow({ ...payrollNoveltyFlow, users: newList });

        onCloseUserApproversModal();
    };

    const onCloseConfirmDeleteModal = () => {
        setDetailsConfirmModal((prev) => ({ ...prev, isOpen: false }));
    };

    const onCloseUserApproversModal = () => {
        setUsersApproversModal((prev) => ({ ...prev, isOpen: false }));
    };

    const onCloseGroupsModal = () => {
        setGroupsModal((prev) => ({ ...prev, isOpen: false }));
    };

    const openConfirmDeleteModal = () => {
        setDetailsConfirmModal({
            isOpen: true,
            onConfirm: onDeletePayrollNoveltyFlow,
            text: '¿Realmente desea eliminar este flujo de aprobación?',
            title: 'Eliminar flujo de aprobación',
            onClose: onCloseConfirmDeleteModal,
        });
    };

    const openUserApproversModal = () => {
        setUsersApproversModal({
            isOpen: true,
            onConfirm: onAddUserApproversFlow,
            onClose: onCloseUserApproversModal,
            data: usersApprovers,
            dataSelected: payrollNoveltyFlow?.users,
        });
    };

    const openGroupsModal = () => {
        setGroupsModal({
            isOpen: true,
            onConfirm: onAddGroupFlow,
            onClose: onCloseGroupsModal,
            data: groupsTreeList,
            dataSelected: payrollNoveltyFlow?.groups,
        });
    };

    return (
        <>
            {detailsConfirmModal.isOpen && <ConfirmModal {...detailsConfirmModal} />}
            {usersApproversModal.isOpen && <AddUserModal {...usersApproversModal} />}
            {groupsModal.isOpen && <AddGroupTreeModal {...groupsModal} />}

            <form method='post' className='p-3 py-1' onSubmit={(e) => onSubmit(e)}>
                {errorMessage && showErrorMessage && (
                    <Alert variant='danger'>{errorMessage}</Alert>
                )}

                <Row className='g-2 mb-4'>
                    <Col md={6}>
                        <TextField
                            label='Nombre'
                            required
                            name='name'
                            onChange={handleInputChange}
                            value={payrollNoveltyFlow.name}
                        />
                    </Col>

                    <Col md={8}>
                        <CheckboxField
                            label='Aplica para todos los grupos'
                            name='isForAllGroups'
                            onChange={handleInputChange}
                            value={payrollNoveltyFlow.isForAllGroups}
                        />
                    </Col>

                    <Col
                        md={12}
                        className='row  mt-3 justify-content-between align-items-top gap-5'
                    >
                        <div className='col-7 flex-1 border p-3'>
                            <h5 className='text-primary2 d-flex gap-1 align-items-center'>
                                Agregar grupos
                                {!payrollNoveltyFlow.isForAllGroups && (
                                    <i
                                        className='ri-add-circle-fill fs-2'
                                        style={{ cursor: 'pointer' }}
                                        onClick={openGroupsModal}
                                    ></i>
                                )}
                            </h5>
                            <hr />

                            {payrollNoveltyFlow.isForAllGroups ? (
                                <p className='alert alert-success mt-2'>
                                    Todos los grupos seran asociados
                                </p>
                            ) : payrollNoveltyFlow?.groups?.length > 0 ? (
                                <div className='table-responsive'>
                                    <Table className='table-striped table-nowrap align-middle mb-0 text-dark'>
                                        <thead>
                                            <tr>
                                                <th scope='col'>Grupo</th>
                                                <th scope='col' className='text-end'>
                                                    Editar
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className='text-dark '>
                                            {payrollNoveltyFlow.groups.map(
                                                (group, i) => (
                                                    <tr key={group.groupId}>
                                                        <td className='text-dark'>
                                                            {group.groupName}
                                                        </td>
                                                        <td>
                                                            <span
                                                                style={{
                                                                    cursor: 'pointer',
                                                                }}
                                                                className='justify-content-end text-muted hover d-flex gap-1 align-items-center'
                                                                onClick={() =>
                                                                    handlerDeleteGroup(
                                                                        group.groupId
                                                                    )
                                                                }
                                                            >
                                                                <i className='fs-15  ri-delete-bin-line'></i>
                                                                Eliminar
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <p className='mt-2'>No hay grupos asociados...</p>
                            )}
                        </div>

                        <div className='col-5  border p-3'>
                            <h5 className='text-primary2  d-flex gap-1 align-items-center'>
                                Fases de aprobación
                                <i
                                    onClick={openUserApproversModal}
                                    className='ri-add-circle-fill fs-2'
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </h5>
                            <hr />

                            {payrollNoveltyFlow?.users?.length > 0 ? (
                                <div className=''>
                                    {payrollNoveltyFlow.users.map((user, i) => (
                                        <h6
                                            key={i}
                                            className='text-dark d-flex justify-content-between gap-2 align-items-center fw-light'
                                        >
                                            <span>
                                                {user.stage} {'- '}{' '}
                                                {user.fullName.toUpperCase()}{' '}
                                            </span>
                                            <span>
                                                {renderOrderElements(user, i)}
                                            </span>
                                        </h6>
                                    ))}
                                </div>
                            ) : (
                                <p>No hay usuarios asociados...</p>
                            )}
                        </div>
                    </Col>
                </Row>

                <div className='d-flex gap-2'>
                    <BackButton to={Private_Routes.PAYROLL_NOVELTY_FLOWS.index} />
                    <SaveButton
                        isLoading={isSaving}
                        isSubmit
                        isDisable={
                            errorMessage && errorMessage?.length > 0
                                ? true
                                : false || isDeleting
                        }
                    />

                    {!isNew && (
                        <DeleteButton
                            isLoading={isDeleting}
                            isSubmit={false}
                            handlerSubmit={openConfirmDeleteModal}
                            isDisable={
                                errorMessage && errorMessage?.length > 0
                                    ? true
                                    : false || isSaving
                            }
                        />
                    )}
                </div>
            </form>
        </>
    );
};
